import { DOCUMENT, isPlatformServer } from '@angular/common';
import { HttpUrlEncodingCodec } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  AuthConfig,
  BaseSiteService,
  CmsComponent,
  CmsService,
  EventService,
  LanguageService,
  Product,
  ProductService,
  WindowRef
} from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  Cart,
  CartRemoveEntrySuccessEvent,
  CartUpdateEntryFailEvent,
  CartUpdateEntrySuccessEvent
} from '@spartacus/cart/base/root';
import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import {
  BrandsEnum,
  BrandThemesEnum,
  CoreModalService,
  DeviceService,
  IDropdownAccountData, IDropdownCart,
  IHeaderIconLink,
  IIconLabel,
  ILanguageSelectorData,
  ILink,
  IMenuDataConfig,
  INavigationNode, IPageNavigation,
  IProductCard,
  IProductCustomer,
  IPromo,
  IPushCard,
  IPushCardWithLayout,
  ISearch,
  ISearchData, ITabBrand,
  ScrollSpyService,
  SdButtonComponent,
  SdHeaderComponent,
  SdHeaderWithTabsComponent,
  SdModalDefaultComponent,
  StatusEnum,
  SubcategoryTypeEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import {
  ProductNotificationModalComponent
} from '../../../components/static/product-notification-modal/product-notification-modal.component';
import {
  OmnibusRecommendedPriceModalComponent
} from '../../../components/static/omnibus-recommended-price-modal/omnibus-recommended-price-modal.component';
import { WebCommonConfig } from '../../../config/configs/web-common.config';
import {
  Bundle,
  CartGroupEntry,
  CustomCmsLinkComponent, CustomCmsNavigationNode,
  CustomMedia, CustomPromotionResult,
  HeaderModuleInterface, NavigationBlocModuleInterface,
  Profil, PromotionAction, PromotionCMSData, PushCardComponentInterface, RichAutocompleteResult,
  UrlMapping, WC_CONFIG_PROVIDER, WcConfig
} from '../../../models';
import {
  CartGroupTypeEnum,
  NavigationNodeEntryTypeEnum,
  OrderTypeEnum, ProductScopeEnum,
  StockStatusEnum
} from '../../../enums';
import { LinkMapper, OrderMapper, PushCardMapper, SearchMapper } from '../../../mappers';
import {
  BundleService, CartFacadeService, CheckoutStepperService, ComponentService, ConfigurationService,
  CustomAuthService, CustomLanguageService, DatalayerService, GlobalSearchService, HobStateService, ImageService,
  LoaderService, MyAccountService,
  PdpNavigationService, RecipeService, ToasterService, TrackingService, UrlMappingService
} from '../../../services';
import { deepEqualWithoutTime } from '../../../utils';
import { AbstractComponent } from '../abstract-component.component';
import { CUSTOM_LANGUAGE } from '@seb/web-common/core/constants';

@Component({
  selector: 'wc-header-module',
  templateUrl: './header-module.component.html',
  standalone: true,
  imports: [
    SdHeaderComponent,
    SdModalDefaultComponent,
    SdButtonComponent,
    ProductNotificationModalComponent,
    OmnibusRecommendedPriceModalComponent,
    TranslateModule,
    SdHeaderWithTabsComponent
  ],
  providers: [CoreModalService]
})
export class HeaderModuleComponent extends AbstractComponent<HeaderModuleInterface> implements OnInit, OnDestroy {
  @ViewChild(SdHeaderComponent)
  headerComponent: SdHeaderComponent;

  alpha: boolean;
  themesConfig: ThemesEnum.light | ThemesEnum.dark | BrandThemesEnum.brand;
  showSticky = false;
  iconLabel: IIconLabel[];
  dropDownAccountData: IDropdownAccountData;
  dropdownHelpData: ILink[];
  dropDownCart: IDropdownCart = {};
  searchData: ISearchData;
  headerIconLink: IHeaderIconLink[] = [];

  menuDataConfig: IMenuDataConfig = {
    menuData: {
      navigationNode: {
        children: [{ uid: '', title: '', linksList: [{}] }],
        linksList: [],
        type: SubcategoryTypeEnum.defaultLinkList,
        title: '',
        uid: '',
        others: {},
        brand: null,
        highlight: false
      }
    },
    titleLevel: 5
  };

  languageSelectorData: ILanguageSelectorData = { languages: [] };

  logoLink: ILink = {
    url: '/',
    external: false
  };

  pageNavigationData: IPageNavigation;
  searchedTerm: string;
  isMenuOpen: boolean;

  currentProduct: Product = {};
  currentBundle: Bundle = {};
  logoutIframeSrc: SafeResourceUrl;

  cart: Cart;
  logoutIframeOpened = false;
  isSimplified = false;
  notifyModalOpened = false;
  notifyProduct = '';
  notifyProductName = '';
  isBundle = false;
  isSearchInputTouched = false;
  inputMinLength = 3;
  cartId: string;
  editProfil$: Subscription;
  products: IProductCustomer[];
  cartEntries: CartGroupEntry[];
  pushs: IPushCardWithLayout[] = [];
  pushPromoTracking: PromotionCMSData[] = [];
  pushPromoTracking$ = new Subject<PromotionCMSData[]>();
  miniCartTracking$ = new Subject<Cart>();
  hasOneHighlight = false;

  quantityDisabled = false;
  isModalEmptyMinicartOpened: boolean;
  isOpenedRecommendedModal = false;
  isNewPriceManagementActivated = false;
  isTaxesIncluded = false;
  cmpHeaderComponent: HeaderModuleInterface;
  typingTimer: any;
  richAutocompleteDelay = 600;
  isSSR = true;
  brand: BrandsEnum;
  activeBrand: string = BrandsEnum.default;
  activeBrands: ITabBrand[] = [];
  isHob = false;
  discoverBrandsText: string;

  private readonly context = 'Main Product';
  private readonly miniCartContext = 'Mini Cart';
  private mediaContainerSubscriptions: Subscription[] = [];
  private config: WebCommonConfig;

  constructor(
    private componentService: ComponentService,
    private cmsService: CmsService,
    private languageService: LanguageService,
    private cdRef: ChangeDetectorRef,
    private imageService: ImageService,
    private translateService: TranslateService,
    private authService: CustomAuthService,
    private domSanitizer: DomSanitizer,
    private configService: ConfigurationService,
    private activeCartService: ActiveCartService,
    private myAccountService: MyAccountService,
    private orderMapper: OrderMapper,
    private router: Router,
    private authConfig: AuthConfig,
    private currentProductService: CurrentProductService,
    private pdpNavigationService: PdpNavigationService,
    private scrollSpyService: ScrollSpyService,
    private trackingService: TrackingService,
    private readonly cartFacadeService: CartFacadeService,
    private readonly route: ActivatedRoute,
    private readonly bundleService: BundleService,
    private globalSearchService: GlobalSearchService,
    private recipeService: RecipeService,
    private searchMapper: SearchMapper,
    private pushCardMapper: PushCardMapper,
    private toasterService: ToasterService,
    private loaderService: LoaderService,
    private readonly multiCartService: MultiCartService,
    private eventService: EventService,
    private deviceService: DeviceService,
    private urlMappingService: UrlMappingService,
    private checkoutStepper: CheckoutStepperService,
    private readonly datalayerService: DatalayerService,
    private baseSiteService: BaseSiteService,
    private readonly productService: ProductService,
    private readonly hobStateService: HobStateService,
    private customLanguageService: CustomLanguageService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(WC_CONFIG_PROVIDER) protected wcConfig: WcConfig,
    @Optional() private windowRef: WindowRef
  ) {
    super();

    this.config = this.configService.config;
    this.isSSR = isPlatformServer(this.platformId);

    this.subscriptions.add(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.updateActiveCat();
        this.headerComponent?.menuComponent?.closeMenu();
      })
    );
  }

  override mandatoryFunction = (cmp: HeaderModuleInterface): boolean => {
    return !!cmp.topHeaderLinks && !!cmp.myAccountLinks && !!cmp.mainNavNode.navigationNode;
  };

  ngOnInit(): void {
    this.initMenuAndAccount();
    this.initLanguages();
    this.initHeaderTheme();
    this.initCartDropdown();

    if (!this.isSSR) {
      this.initStickyPDP();
    }

    const baseStore = this.baseStoreService.baseStore;

    if (baseStore.houseOfBrandConfig?.brand) {
      this.subscriptions.add(
        this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand, activeBrands }) => {
          this.activeBrand = liveBrand || siteBrand;
          this.isHob = isHob;
          this.enableHOB(baseStore.brandSite, activeBrands);
          this.cdRef.markForCheck();
        })
      );
    }

    this.isNewPriceManagementActivated = baseStore.activateNewPriceManagement;
    this.isTaxesIncluded = baseStore.displayTaxesIncluded;

    if (baseStore.richAutocompleteMinLength) {
      this.inputMinLength = baseStore.richAutocompleteMinLength;
    }

    if (baseStore.richAutocompleteDelay) {
      this.richAutocompleteDelay = baseStore.richAutocompleteDelay;
    }

    this.initHeaderAlpha();

    this.subscriptions.add(
      this.eventService.get(CartUpdateEntrySuccessEvent).subscribe((event) => {
        this.cartFacadeService.cartProductUpdateEntrySuccess(event);
      })
    );

    this.subscriptions.add(
      this.eventService.get(CartUpdateEntryFailEvent).subscribe((event) => {
        this.cartFacadeService.cartProductUpdateEntryFail(event);
      })
    );

    this.subscriptions.add(
      this.eventService.get(CartRemoveEntrySuccessEvent).subscribe((event) => {
        this.cartFacadeService.cartProductRemoveEntrySuccessEvent(event);
        this.loaderService.hideGlobalLoader();
      })
    );

    this.subscriptions.add(
      this.cmsService.getContentSlot('MiniCart').pipe(
        filter((slot) => slot.components?.length > 0),
        switchMap((slot) => this.cmsService.getComponentData(slot.components[0].uid)),
        filter((cms) => !!cms),
        switchMap((cms) => this.componentService.getLazyComponentData(cms, 'links')),
        switchMap((componentData: PushCardComponentInterface) => {
          let media$: Observable<CustomMedia>;

          if (componentData.mediaContainer) {
            media$ = this.imageService.getMediaFromContainer(componentData.mediaContainer);
          } else {
            media$ = of(componentData.media);
          }

          return combineLatest([of(componentData), media$]);
        })
      ).subscribe(([componentData, media]) => {
        this.pushs = [
          this.pushCardMapper.toPushCardWithLayout({
            ...componentData,
            media
          })
        ];
        this.dropDownCart = {
          ...this.dropDownCart,
          pushs: this.pushs
        };
      })
    );
  }

  manageOpenedMenu(event: boolean): void {
    this.isMenuOpen = event;

    this.updateClass('has-menu-scrolled', event);
  }

  onScrolled(event: boolean): void {
    this.updateClass('is-scrolled', event);
  }

  updateClass(className: string, event: boolean): void {
    const bodyElement = this.document.body;
    if (bodyElement) {
      if (event === true) {
        if (bodyElement.classList.contains(className)) {
          this.renderer.removeClass(bodyElement, className);
        }
      } else {
        this.renderer.addClass(bodyElement, className);
      }
    }
  }

  login(): void {
    this.authService.toggleLoginWithRedirect();
  }

  logout(): void {
    this.myAccountService.deleteProfile();
    this.authService.coreLogout();
    this.logoutIframeSrc = this.domSanitizer.bypassSecurityTrustResourceUrl(
      this.authConfig.authentication.baseUrl + this.authConfig.authentication.logoutUrl
    );
    this.logoutIframeOpened = true;
    this.router.navigate(['']);
  }

  closeLogoutIframe(): void {
    this.logoutIframeOpened = false;
  }

  setLanguage(isocode: string): void {
    this.subscriptions.add(
      this.languageService.getActive().pipe(take(1)).subscribe((currentLang) => {
        if (isocode !== currentLang) {
          this.languageService.setActive(isocode);
          this.windowRef.location.reload();
        }
      })
    );
  }

  isCartOpened(isOpened: boolean): void {
    if (isOpened) {
      this.trackingService.sendEvent('vpLayer', { layerName: 'minicart' });
      this.miniCartTracking$.next(this.cart);
      this.pushPromoTracking$.next(this.pushPromoTracking);
    }
  }

  isAccountOpened(isOpened: boolean): void {
    if (isOpened) {
      this.trackingService.sendEvent('vpLayer', { layerName: 'myaccount' });
    }
  }

  openEmptyMinicartModal(): void {
    this.isModalEmptyMinicartOpened = true;
  }

  emptyDropdownCart(): void {
    const productEvents = this.cartEntries.map((entry) => entry.entries.map((order) => (
      this.trackingService.productToEventProduct(order?.product, undefined, order?.quantity)
    ))).reduce(
      (productsArray1, productsArray2) => productsArray1.concat(productsArray2), []
    );

    this.trackingService.sendEvent('clickRemoveFromCart', productEvents);
    this.cartFacadeService.deleteActiveCart();
    this.isModalEmptyMinicartOpened = false;
    this.loaderService.hideGlobalLoader();
  }

  onClearInputSearch(): void {
    this.searchData = {};
  }

  search(event: string): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }

    this.typingTimer = setTimeout(() => {
      this.typingTimer = undefined;
      this.isSearchInputTouched = !!event;

      const baseStore = this.baseStoreService.baseStore;

      this.hobStateService.isRecipeEnabled$.pipe(
        switchMap((isRecipeEnabled) => combineLatest([
          this.globalSearchService.autocompleteSearch(event).pipe(
            catchError(() => of(undefined))
          ),
          isRecipeEnabled ? (
            this.recipeService.search(event, 0, this.config.autocompleteRecipesSize, {
              sort: {
                name: 'popularity',
                direction: 'DESC'
              }
            }).pipe(
              catchError(() => of(undefined))
            )
          ) : of(undefined)
        ]))
      ).subscribe(([globalResult, recipeResult]) => {
        this.searchedTerm = event;
        let data: ISearch = {};

        if (globalResult?.rich) {
          if (globalResult.results) {
            const globalResultResults = globalResult.results;
            const accessories = (globalResultResults as RichAutocompleteResult)?.accessories?.filter((productResult) =>
              new RegExp(`(${productResult.code}|${productResult.commercialCode})`, 'i').test(event)
            );
            const products = (globalResultResults as RichAutocompleteResult)?.products?.filter((productResult) =>
              new RegExp(`(${productResult.code}|${productResult.commercialCode})`, 'i').test(event)
            );
            const global =
              baseStore.isSearchRedirectionByCMMFAndRefEnabled && (accessories.length || products.length)
                ? {
                  ...globalResultResults,
                  accessories,
                  products,
                  totalAccessories: accessories.length,
                  totalProducts: products.length
                }
                : globalResultResults;
            data = this.searchMapper.mapRichAutocompleteResultToISearchData(
              global as RichAutocompleteResult, this.isTaxesIncluded, event, this.isNewPriceManagementActivated, this.isHob
            ).data;
          }
        }

        if (recipeResult) {
          data = {
            ...data,
            ...this.searchMapper.mapRecipeSearchResultToISearchData(recipeResult, event).data
          };
        }

        this.searchData = { data };
        this.updateSearchPrices();
        this.cdRef.markForCheck();
      });

      this.trackingService.sendEvent('search', {
        request: event,
        type: 'not set'
      });

    }, this.richAutocompleteDelay);
  }

  private updateItemsPrices<T extends IProductCard>(items: T[], _itemType: 'products' | 'accessories'): Observable<T>[] {
    if (items && items?.length > 0) {
      return items
        .filter((item) => !!item)
        .map((item) =>
          this.productService.get(item.code, ProductScopeEnum.PRICE).pipe(
            filter((priceObj) => !!priceObj),
            map((priceObj) => {
              const formattedPrices = this.searchMapper.formatPrice(priceObj, this.isNewPriceManagementActivated);
              return {
                ...item,
                price: formattedPrices?.price,
                discountedPrice: formattedPrices?.discountedPrice,
                recommendedPrice: formattedPrices?.recommendedPrice
              };
            })
          )
        );
    } else {
      return [];
    }
  }

  private enableHOB(host: string, brands: BrandsEnum[]): void {
    this.baseSiteService.get().pipe(
      take(1),
      switchMap((baseSite) => {
        const needLang = baseSite?.urlEncodingAttributes?.includes(CUSTOM_LANGUAGE);
        if (needLang) {
          return this.customLanguageService.getActive().pipe(
            take(1),
            map((lang) => lang.split('_')[0]),
            map((language) => ({ language }))
          );
        } else {
          return of({ language: '' });
        }
      })
    ).subscribe(({ language }) => {
      this.activeBrands = brands.map((brand) => ({
        url: brand !== host ? `${language}/${brand.toLowerCase()}` : `${language}/`,
        brand
      }));

      this.discoverBrandsText = this.translateService.instant('hob.discover.brands.text').trim();
      this.brand = BrandsEnum.hob;
    });
  }

  private updateSearchData<T extends IProductCard>(updatedItems: T[], itemType: 'products' | 'accessories'): void {
    const updatedItemsMap = new Map(updatedItems.map((item) => [item.code, item]));
    const updatedItemsList = this.searchData?.data?.[itemType]?.items.map((item) => {
      if (updatedItemsMap.has(item?.code)) {
        return { ...item, ...updatedItemsMap.get(item?.code) };
      }
      return item;
    });

    this.searchData = {
      data: {
        ...this.searchData?.data,
        [itemType]: {
          ...this.searchData?.data?.[itemType],
          items: updatedItemsList
        }
      }
    } as ISearchData;
  }

  private updateSearchPrices(): void {
    const updateSearchProductsPrices$ = this.updateItemsPrices(this.searchData?.data?.products?.items, 'products');
    const updateSearchAccessoriesPrices$ = this.updateItemsPrices(this.searchData?.data?.accessories?.items, 'accessories');

    combineLatest(updateSearchProductsPrices$).pipe(take(1)).subscribe((updatedSearchProducts: IProductCard[]) => {
      this.updateSearchData(updatedSearchProducts, 'products');
    });

    combineLatest(updateSearchAccessoriesPrices$).pipe(take(1)).subscribe((updatedSearchAccessories: IProductCard[]) => {
      this.updateSearchData(updatedSearchAccessories, 'accessories');
    });
  }

  submitSearch(event: string): void {
    this.trackingService.sendEvent('search', { request: event, type: 'not set' });
    this.router.navigate(['/search'], {
      queryParams: {
        query: event,
        text: event,
        sortCode: 'relevance',
        page: null,
        currentPage: null
      },
      queryParamsHandling: 'merge'
    });
  }

  changeQuantity(event: { product: string; qty: number }): void {
    const currentProduct = this.products;
    const standaloneEntries = this.cartEntries?.filter((entry) => entry.type === CartGroupTypeEnum.STANDALONE);
    const standaloneEntry = standaloneEntries?.find(
      (entry) => entry.entries[0]?.product?.commercialCode === event.product
    );

    if (!!standaloneEntry && standaloneEntry.entries?.length > 0) {
      if (event.qty !== standaloneEntry.entries[0]?.quantity) {
        this.activeCartService.updateEntry(standaloneEntry.entries[0]?.entryNumber, event.qty);
        sessionStorage.setItem(
          'addToCartContext',
          sessionStorage.getItem('addToCartContext')
            ? sessionStorage.getItem('addToCartContext')
            : this.miniCartContext
        );
      }
    } else {
      const bundleEntries = this.cartEntries.filter((entry) => entry.type === CartGroupTypeEnum.BUNDLE);
      const bundleEntry = bundleEntries.find((entry) => entry.externalReferenceId === event.product);
      let isLowStock = false;

      if (event.qty !== bundleEntry.selectedQuantity) {
        this.cartFacadeService
          .updateBundle(bundleEntry.externalReferenceId, event.qty)
          .pipe(takeUntil(this.destroy$))
          .subscribe((result) => {
            isLowStock = result?.cartModifications?.some((cart) => cart.statusCode !== 'success');
            if (isLowStock) {
              this.toasterService.sendToasterEvent({
                status: StatusEnum.error,
                title: 'cart.toast.updateQuantityBundleToasterTitleError',
                description: 'cart.toast.updateQuantityBundleToasterDescriptionError'
              });
              this.products = [...currentProduct];
              this.cdRef.markForCheck();
            }
            this.cartFacadeService.cartBundleUpdateEntrySuccess(event.qty, bundleEntry);
            sessionStorage.setItem(
              'addToCartContext',
              sessionStorage.getItem('addToCartContext')
                ? sessionStorage.getItem('addToCartContext')
                : this.miniCartContext
            );
            this.multiCartService.reloadCart(this.cartId);
          });
      }
    }
  }

  removeCartEntry(productCommercialCode: string): void {
    this.loaderService.showGlobalLoader();

    this.activeCartService.getActive()
      .pipe(take(1))
      .subscribe((cart: Cart) => {
        const standaloneEntries = cart?.entryGroups.filter((group) => group.type === CartGroupTypeEnum.STANDALONE);
        const standaloneEntry = standaloneEntries?.find(
          (entry) => entry.entries[0]?.product?.commercialCode === productCommercialCode
        );
        if (!!standaloneEntry && standaloneEntry.entries?.length > 0) {
          this.activeCartService.removeEntry(standaloneEntry.entries[0]);
        } else {
          const bundleEntries = cart?.entryGroups.filter((group) => group.type === CartGroupTypeEnum.BUNDLE);
          const bundleEntry = bundleEntries?.find((entry) => entry.externalReferenceId === productCommercialCode);
          if (bundleEntry) {
            let isNotRemoved = false;
            this.cartFacadeService.cartBundleRemoveEntrySuccessEvent(bundleEntry);
            this.cartFacadeService.updateBundle(bundleEntry.externalReferenceId, 0).subscribe((result) => {
              isNotRemoved = result?.cartModifications?.some(
                (cartResult) => cartResult.statusCode !== 'succesfullyRemoved'
              );
              if (!isNotRemoved) {
                this.multiCartService.reloadCart(this.cartId);
                this.loaderService.hideGlobalLoader();
              }
            });
          }
        }
      });
  }

  initStickyPDP(): void {
    const page$ = this.cmsService.getCurrentPage();
    const activeSpyTarget$ = this.scrollSpyService.activeSpyTarget.pipe(distinctUntilChanged());

    combineLatest([page$, activeSpyTarget$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([page, activeSpyTarget]) => {
        this.showSticky = page && page.template?.endsWith('ProductDetailsPageTemplate') && activeSpyTarget !== 'overview';
        this.cdRef.markForCheck();
      });

    this.route?.queryParamMap
      ?.pipe(
        map((param) => param.get('bundle')),
        switchMap((bundleCode) =>
          bundleCode ? this.bundleService.getBundle(bundleCode, 'bundle', true) : of(undefined)
        ),
        switchMap((bundle) => {
          if (bundle) {
            this.isBundle = true;
            this.setBundleToSticky(bundle);
            return of(undefined);
          } else {
            this.isBundle = false;
            return this.currentProductService.getProduct();
          }
        }),
        filter((p) => !!p),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (product: Product) => {
          this.currentProduct = product;
          const isInStock =
            product?.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
            product?.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK;

          this.pageNavigationData = {
            title: '',
            links: this.pdpNavigationService.getActiveNavigation(product),
            product: {
              title: product?.name,
              code: product?.code,
              stock: {
                online: isInStock
              },
              onSale: product?.onSale,
              price: product?.price?.formattedValue,
              discountedPrice:
                product?.discountedPrice?.formattedValue !== product?.price?.formattedValue
                  ? product?.discountedPrice?.formattedValue
                  : '',
              recommendedPrice:
                product?.recommendedPrice?.formattedValue !== product?.price?.formattedValue && this.isNewPriceManagementActivated
                  ? product?.recommendedPrice?.formattedValue
                  : '',
              isDiscountWithRecommendedPrice: !this.isNewPriceManagementActivated ? !product?.strikethroughPrice : false
            }
          };

          this.cdRef.markForCheck();
        },
        () => this.loaderService.hideGlobalLoader()
      );
  }

  addToCartEntry(): void {
    sessionStorage.setItem('addToCartContext', this.context);
    if (this.isBundle) {
      this.cartFacadeService.addBundleToCartShippingType(this.currentBundle, 1);
    } else {
      this.cartFacadeService.addToCartShippingType(this.currentProduct, 1);
    }
  }

  cartNotification(): void {
    this.notifyProductName = this.currentProduct.name;
    this.notifyProduct = this.currentProduct.code;
    this.notifyModalOpened = true;
  }

  checkoutPreviousStep(): void {
    this.checkoutStepper.goBack();
  }

  onClickMenu(navLevel: string): void {
    const categoryData = this.menuDataConfig?.menuData?.navigationNode?.children.filter(
      (category) => category.title === navLevel
    )[0];
    const pushs = categoryData?.others?.pushs;
    if (pushs) {
      for (let i = 0; i < pushs.length; i++) {
        if (pushs[i]?.push.isPromo) {
          this.trackingService.sendEvent('promotionView', {
            promotionName: pushs[i]?.push?.richParagraph.heading.data.title,
            promotionPosition: 'push promo menu',
            promotionType: 'Menu'
          });
        }
      }
    }
  }

  onSearchElementClicked(event: any) {
    this.sendSearchSugestion(event);
  }

  sendSearchSugestion(event: string) {
    let decodedValue: string;
    let type: string;
    let request: string;
    if (!event?.includes('/c/')) {
      decodedValue = new HttpUrlEncodingCodec().decodeValue(event).replace(/-/gi, ' ');
      if (decodedValue.includes('/p/')) {
        type = 'Product';
      } else if (decodedValue.includes('/accessory/')) {
        type = 'Accessory';
      } else if (decodedValue.includes('/recipe/')) {
        type = 'Recipe';
      }
      request = this.searchedTerm;
      if (type) {
        this.trackingService.sendEvent('searchSuggestion', {
          request,
          type
        });
      }
    } else if (event.includes('/c/')) {
      request = this.searchedTerm;
      type = 'Category';
      this.trackingService.sendEvent('searchSuggestion', {
        request,
        type
      });
    }
  }

  setCurrentCart(currentCart: Cart, cmsService: CmsService): void {
    if (currentCart) {
      const products: IProductCard[] = [];
      this.quantityDisabled = currentCart.isForfait;
      this.cartEntries = currentCart?.entryGroups;
      currentCart.entryGroups?.forEach((group) => {
        if (group.type === CartGroupTypeEnum.STANDALONE) {
          const product = this.orderMapper.orderEntryToIOrderProduct(
            group.entries[0], undefined, undefined, undefined, undefined, undefined, undefined,
            this.isNewPriceManagementActivated
          ).productInfo;
          const productPromo = currentCart?.appliedProductPromotions?.find((promo) =>
            promo?.consumedEntries?.some((entry) => group?.entries[0]?.entryNumber === entry?.orderEntryNumber)
          );

          product['promo'] = productPromo?.promotion?.description;
          product.price = group.entries[0]?.totalPriceWithoutDiscounts?.formattedValue;
          product.discountedPrice =
            group.entries[0]?.totalPriceWithoutDiscounts?.value !== group.entries[0]?.totalPrice?.value
              ? group.entries[0]?.totalPrice?.formattedValue
              : undefined;
          product.isTaxesIncluded = this.isTaxesIncluded;
          products.push(product);
        } else if (group.type === CartGroupTypeEnum.BUNDLE) {
          if (group.entries?.length > 0) {
            const product = this.orderMapper.cartGroupEntryToIProductCustomer(group);
            product.price = group.totalPriceWithoutDiscounts?.formattedValue;
            product.discountedPrice =
              group.totalPriceWithoutDiscounts?.value !== group.totalPrice?.value
                ? group.totalPrice?.formattedValue
                : undefined;
            product.isTaxesIncluded = this.isTaxesIncluded;
            product.bundle = group.entries?.map((entryBundle) => {
              return this.orderMapper.orderEntryToIOrderProduct(
                entryBundle, undefined, undefined, undefined, undefined, undefined, undefined, this.isNewPriceManagementActivated
              ).productInfo;
            });
            products.push(product);
          }
        }
      });

      this.products = products;
      let promotionActionsGroups: PromotionAction[][] = [];
      const promotionCMSComponentIDsGroups: string[][] = [];
      const properData: PromotionCMSData[] = [];

      if (!!currentCart.appliedOrderPromotions && currentCart.appliedOrderPromotions.length !== 0) {
        const promos = currentCart.appliedOrderPromotions;
        promotionActionsGroups = promos
          .filter((promo: CustomPromotionResult) => promo?.promotion?.ruleGroup === 'specialOfferRuleGroup')
          .map((p: CustomPromotionResult) => p?.promotionActions)
          .slice(0, 3);

        promotionActionsGroups.forEach((promotionActionsGroup) => {
          promotionActionsGroup.forEach((promotionAction: PromotionAction) => {
            if (!!promotionAction.cmsComponentsIDs && promotionAction.cmsComponentsIDs.length > 0) {
              promotionCMSComponentIDsGroups.push(promotionAction.cmsComponentsIDs.slice(0, 1));
            }
          });
        });
        const promotionIDs: string[] = [...new Set(promotionCMSComponentIDsGroups.flat(1))];
        const promotionsPushDataList: PromotionCMSData[] = [];
        const cmsComponentsObservables: Observable<any>[] = [];
        promotionIDs.forEach((promotionID) => {
          cmsComponentsObservables.push(cmsService.getComponentData(promotionID));
        });

        combineLatest(cmsComponentsObservables).subscribe((obs) => {
          obs.forEach((cmp: any) => {
            const promoCMSData: PromotionCMSData = {
              uid: cmp?.uid,
              promo: cmp?.title,
              text: cmp?.message,
              link: {
                url: cmp?.urlLink,
                linkName: cmp?.urlName
              }
            };
            promotionsPushDataList.push(promoCMSData);
          });
          const promoIds = promotionsPushDataList.map((o) => o.uid);
          const uniquePromos = promotionsPushDataList.filter(({ uid }, index) => !promoIds.includes(uid, index + 1));
          this.pushPromoTracking = uniquePromos;

          properData.length = 0;
          uniquePromos.forEach((e) => {
            const item = { ...e };
            delete item?.uid;
            properData.push(item);
          });
        });
      } else {
        this.pushPromoTracking = [];
      }
      this.dropDownCart = {
        cartUrl: '/cart',
        totalPrice: currentCart.totalPrice?.formattedValue,
        shipping: !currentCart.isForfait
          ? {
            info: this.getShippingInfo(currentCart),
            cost: currentCart.deliveryCost?.formattedValue
          }
          : null,
        paymentCost: currentCart.paymentCost?.formattedValue,
        isShippingFree:
          (currentCart.isForfait && !currentCart.serviceShippingIncluded) || currentCart.freeShippingApplied,
        isPaymentFree: !currentCart.paymentCost || currentCart.paymentCost?.value === 0,
        products,
        quantityDisabled: currentCart.isForfait,
        pushs: this.pushs,
        promos: !!properData && properData
      };
      this.cart = currentCart;
      this.cdRef.markForCheck();
    }
  }

  getShippingInfo(currentCart: Cart): string {
    let shippingInfo;
    if (currentCart.orderType === OrderTypeEnum.EXTLOG) {
      shippingInfo = this.translateService.instant('cart.minicart.shipping.EXTLOG');
    } else if (!currentCart.isForfait) {
      shippingInfo = this.translateService.instant('cart.minicart.shipping.SAP');
    }
    return shippingInfo;
  }

  setCurrentCartTracking(): void {
    this.miniCartTracking$.pipe(takeUntil(this.destroy$)).subscribe((cart: Cart) => {
      this.trackingService.sendEvent('clickMiniCart', {
        cartID: cart?.guid,
        cartCurrency: cart.totalPrice?.currencyIso,
        cartSubTotal: cart.subTotal?.value,
        cartTotal: cart.totalPrice?.value,
        position: 'mini cart'
      });
    });
    this.pushPromoTracking$.pipe(takeUntil(this.destroy$)).subscribe((promos: PromotionCMSData[]) => {
      promos.forEach((promoData) => {
        this.trackingService.sendEvent('promotionView', {
          promotionName: promoData.promo,
          promotionPosition: 'push promo minicart'
        });
      });
    });
  }

  private convertNavigationNode(node: CustomCmsNavigationNode): Observable<INavigationNode> {
    const result: INavigationNode = {};
    const brandCornerList = JSON.stringify(this.baseStoreService.baseStore?.brandsCorner);
    const brands = brandCornerList && brandCornerList !== 'undefined' ? JSON.parse(brandCornerList) : undefined;

    if (
      BrandsEnum[node?.brandCorner?.toLowerCase()] &&
      (!brands || !brands?.find((brand) => brand.id === node.brandCorner))
    ) {
      return of(null);
    } else {
      result.title = node?.title;
      result.uid = node?.uid;
      result.type = SubcategoryTypeEnum.defaultLinkList;
      result.linksList = [];
      result.others = {};
      result.brand = brands?.find((brand) => brand.id === node?.brandCorner) ? node?.brandCorner : null;
      result.highlight = this.hasOneHighlight ? false : !!node?.highlight;

      if (!this.hasOneHighlight && !!node?.highlight) {
        this.hasOneHighlight = true;
      }

      return of(result).pipe(
        switchMap(() => {
          if (node?.entries?.length > 0) {
            return combineLatest(node?.entries.map((entry) => this.cmsService.getComponentData(entry.itemId).pipe(
              filter((entry) => !!entry),
              take(1)
            )));
          } else {
            return of([]);
          }
        }),
        switchMap((entries: CmsComponent[]) => {
          let navigationBloc$: Observable<NavigationBlocModuleInterface>;
          const pushs$: Observable<PushCardComponentInterface>[] = [];
          entries.forEach((entry) => {
            switch (entry?.typeCode as NavigationNodeEntryTypeEnum) {
              case NavigationNodeEntryTypeEnum.MEDIA:
                result.media = this.imageService.normalizeMedia((entry as { media: CustomMedia }).media);
                break;
              case NavigationNodeEntryTypeEnum.LINK:
                result.linksList.push(LinkMapper.cmsLinkToILink(entry));
                break;
              case NavigationNodeEntryTypeEnum.NAVIGATION_BLOC:
                navigationBloc$ = this.componentService.getLazyComponentData(entry, 'components');
                break;
              case NavigationNodeEntryTypeEnum.PUSH:
                pushs$.push(this.componentService.getLazyComponentData(entry, 'links'));
                break;
            }
          });
          if (result.brand) {
            const brandFound = brands?.find((brand) => brand.id === result.brand);
            this.brandLogo = brandFound?.logo ? brandFound.logo : undefined;
            result.media = brandFound?.logo ? brandFound.logo : undefined;
            result.brandCornerMedia = { url: this.config?.brandCorner?.logo[result.brand.toLowerCase()] };
          }
          return combineLatest([navigationBloc$ || of(undefined), ...pushs$]);
        }),
        tap(([navigationBloc, ...pushs]) => {
          if (navigationBloc !== undefined) {
            result.others.title = navigationBloc.headline;
            result.others.links = navigationBloc?.componentsObjects?.map(LinkMapper.cmsLinkToIExtendedLink) || [];
          }
          if (pushs?.length > 0) {
            result.others.pushs = [];
            pushs.forEach((pushCmp, index) => {
              pushCmp.brandCorner = BrandsEnum[pushCmp?.brandCorner?.toLowerCase()]
                ? pushCmp.brandCorner
                : BrandsEnum.default;
              if (pushCmp.brandCorner && !brands?.find((brand) => brand.id === pushCmp.brandCorner)) {
                pushCmp = null;
              }
              if (pushCmp) {
                if (pushCmp.mediaContainer) {
                  this.mediaContainerSubscriptions[index]?.unsubscribe();
                  this.mediaContainerSubscriptions[index] = this.imageService
                    .getMediaFromContainer(pushCmp.mediaContainer)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((media) => {
                      const customMedia = media ? this.imageService.normalizeMedia(media) : undefined;
                      result.others.pushs[index] = this.pushCardMapper.toPushCardWithLayout(pushCmp, customMedia);
                    });
                } else {
                  result.others.pushs[index] = this.pushCardMapper.toPushCardWithLayout(pushCmp);
                }
              }
            });
            result.others.pushs = result.others.pushs?.filter((push) => push != null);
          }
        }),
        switchMap(() => {
          if (node?.children?.length > 0) {
            return combineLatest(node?.children.map((child) => this.convertNavigationNode(child)));
          } else {
            return of(undefined);
          }
        }),
        tap((children) => {
          result.children = children?.filter((child) => child != null);
        }),
        map(() => result)
      );
    }
  }

  private initMenuAndAccount(): void {
    const module$: Observable<HeaderModuleInterface> = this.componentWithoutLazyLoad$.pipe(
      switchMap((module: HeaderModuleInterface) =>
        combineLatest([
          this.componentService.reloadLazyComponentData(module, 'mainNavNode'),
          this.componentService.getLazyComponentData(module, 'topHeaderLinks'),
          this.componentService.getSubComponentLazyComponentData(module, 'myAccountLinks', 'components'),
          module['storeLocatorLinks']
            ? this.componentService.getSubComponentLazyComponentData(module, 'storeLocatorLinks', 'components')
            : of(module),
          module['needHelpLinks']
            ? this.componentService.getSubComponentLazyComponentData(module, 'needHelpLinks', 'components')
            : of(module)
        ])
      ),
      map(
        ([module1, module2, module3, module4, module5]: [
          HeaderModuleInterface,
          HeaderModuleInterface,
          HeaderModuleInterface,
          HeaderModuleInterface,
          HeaderModuleInterface
        ]) => ({
          ...module1,
          topHeaderLinksObjects: module2.topHeaderLinksObjects,
          myAccountLinks: module3.myAccountLinks,
          storeLocatorLinks: module4.storeLocatorLinks,
          needHelpLinks: module5.needHelpLinks
        })
      ),
      distinctUntilChanged((prev, curr) => deepEqualWithoutTime(prev, curr))
    );
    const isUserLoggedIn$ = this.authService.isUserLoggedIn();

    combineLatest([module$, isUserLoggedIn$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cmp, isUserLoggedIn]) => {
        this.iconLabel = cmp.topHeaderLinksObjects.map((link) => {
          return {
            label: link.linkName,
            iconName: link.picto?.replace(/_/g, '-').toLowerCase(),
            tooltipText: link?.tooltip ?? '',
            link: link ? LinkMapper.cmsLinkToILink(link) : {}
          };
        });
        this.hasOneHighlight = false;
        this.cmpHeaderComponent = cmp;
        this.cdRef.markForCheck();

        this.updateActiveCat();

        this.editProfil$?.unsubscribe();
        const userProfil$ = isUserLoggedIn ? this.myAccountService.getProfil() : of(undefined);
        const userEditedProfil$ =
          isUserLoggedIn && this.myAccountService.editProfilSubject
            ? this.myAccountService.editProfilSubject.pipe(takeUntil(this.destroy$))
            : of(undefined);

        this.editProfil$ = userEditedProfil$?.subscribe((userProfil) => {
          this.accountMapper(
            userProfil,
            isUserLoggedIn,
            cmp.myAccountLinks.componentsObjects.map((link) => LinkMapper.cmsLinkToILink(link))
          );
          this.cdRef.markForCheck();
        });
        userProfil$.subscribe((userProfil) => {
          this.accountMapper(
            userProfil,
            isUserLoggedIn,
            cmp.myAccountLinks.componentsObjects.map((link) => LinkMapper.cmsLinkToILink(link))
          );

          if (!this.isSSR) {
            setTimeout(() => {
              this.datalayerService.updateDataLayer();
            }, 500);
          }

          this.cdRef.markForCheck();
        });

        this.initHeaderIcons(cmp);
      });
  }

  private updateActiveCat() {
    if (this.cmpHeaderComponent) {
      const isTablet$ = this.deviceService.isTablet;
      const isMobile$ = this.deviceService.isMobile;
      let index = -1;

      combineLatest([
        this.convertNavigationNode(this.cmpHeaderComponent.mainNavNode?.navigationNode),
        this.urlMappingService.getUrlMappingByCode(this.router?.url),
        isTablet$,
        isMobile$
      ])
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(
          ([navigationNode, urlMapped, isTablet, isMobile]: [INavigationNode, UrlMapping, boolean, boolean]) => {
            if (navigationNode) {
              if (this.cmpHeaderComponent.needHelpLinks) {
                if (isTablet || isMobile) {
                  if (index !== -1) {
                    navigationNode.children[index - 1] = this.toNavigationNode(this.cmpHeaderComponent.needHelpLinks);
                  } else {
                    index = navigationNode.children?.push(this.toNavigationNode(this.cmpHeaderComponent.needHelpLinks));
                  }
                } else if (!isMobile && !isTablet && index > -1) {
                  navigationNode.children.splice(index, 1);
                }
              }

              const currentPageUrl = urlMapped ? decodeURI(urlMapped?.value) : decodeURI(this.router?.url);
              let currentMainLevel = -1;
              if (currentPageUrl !== '/') {
                for (let i = 0; i < navigationNode.children?.length; i++) {
                  if (navigationNode.children[i].linksList.some((link) => currentPageUrl === link.url)) {
                    currentMainLevel = i;
                    break;
                  } else {
                    for (let j = 0; j < navigationNode.children[i]?.children?.length; j++) {
                      if (
                        navigationNode.children[i]?.children[j].linksList.some((link) => currentPageUrl === link.url)
                      ) {
                        currentMainLevel = i;
                        break;
                      } else {
                        for (let k = 0; k < navigationNode.children[i]?.children[j]?.children?.length; k++) {
                          if (
                            navigationNode.children[i]?.children[j]?.children[k].linksList.some(
                              (link) => currentPageUrl === link.url
                            )
                          ) {
                            currentMainLevel = i;
                            break;
                          }
                        }
                      }
                    }
                  }
                }
              }
              this.menuDataConfig = {
                titleLevel: 5,
                menuData: { navigationNode },
                activeCatId: currentMainLevel > -1 ? navigationNode.children[currentMainLevel]?.uid : null
              };
              this.cdRef.markForCheck();
            }
          }
        );
    }
  }

  private accountMapper(profil: Profil, isLoggedIn, link: ILink[]) {
    this.dropDownAccountData = {
      account: {
        title: 'Account',
        userName: profil ? `${profil?.firstName?.slice(0, 1).toUpperCase()}${profil?.firstName?.slice(1)}` : '',
        links: link
      },
      loggedIn: isLoggedIn
    };
  }

  private initHeaderAlpha(): void {
    this.cmsService
      .getCurrentPage()
      .pipe(
        filter((page) => !!page),
        takeUntil(this.destroy$)
      )
      .subscribe((page) => {
        this.alpha = page['headerDesign'] === 'ALPHA';
        if (this.alpha && !this.isHob) {
          this.renderer.addClass(this.document?.body, 'is-alpha');
        } else {
          if (this.document?.body?.classList.contains('is-alpha')) {
            this.renderer.removeClass(this.document?.body, 'is-alpha');
          }
        }
        if (this.themesConfig === ThemesEnum.dark || this.themesConfig === BrandThemesEnum.brand) {
          this.renderer.addClass(this.document?.body, 'has-header-dark');
        } else if (this.document?.body?.classList.contains('has-header-dark')) {
          this.renderer.removeClass(this.document?.body, 'has-header-dark');
        }

        this.isSimplified =
          page.label?.startsWith('/checkout') ||
          page.label?.startsWith('/cart') ||
          page.label?.startsWith('/donations') ||
          page.label?.startsWith('/order-confirmation') ||
          page.label?.startsWith('/checkout/express/review') ||
          page.label?.startsWith('/received-order-confirmation');

        this.cdRef.markForCheck();
      });
  }

  private initHeaderTheme(): void {
    this.cmsService
      .getCurrentPage()
      .pipe(
        filter((page) => !!page),
        takeUntil(this.destroy$)
      )
      .subscribe((page) => {
        this.themesConfig = ThemesEnum[page['headerTheme']?.toLowerCase()];
        if (this.themesConfig === ThemesEnum.dark || this.themesConfig === BrandThemesEnum.brand) {
          this.renderer.addClass(this.document?.body, 'has-header-dark');
        } else if (this.document?.body?.classList.contains('has-header-dark')) {
          this.renderer.removeClass(this.document?.body, 'has-header-dark');
        }
      });
  }

  private initHeaderIcons(cmp: HeaderModuleInterface) {
    const componentsObjects = cmp.storeLocatorLinks?.componentsObjects.filter((obj) => Object.keys(obj).length !== 0);
    // check if the icon is not already existing
    if (
      cmp.storeLocatorLinks &&
      componentsObjects?.length !== 0 &&
      !this.headerIconLink.find(
        (icon) => icon.iconName === componentsObjects?.[0].picto?.replace('_', '-').toLocaleLowerCase()
      )
    ) {
      this.headerIconLink.push({
        iconOnly: this.config.components?.header?.storeLocator?.iconOnly,
        iconName: componentsObjects?.[0].picto?.replace('_', '-').toLocaleLowerCase() ?? 'icon-world',
        link: {
          url: componentsObjects?.[0].urlLocalized,
          linkName: componentsObjects?.[0].linkName,
          external: false
        }
      });
    }

    if (this.config.components?.header?.help?.isEnabled) {
      if (cmp.needHelpLinks?.componentsObjects?.length >= 2) {
        this.dropdownHelpData = cmp.needHelpLinks?.componentsObjects.map((link) => LinkMapper.cmsLinkToILink(link));
      } else {
        const url = cmp.needHelpLinks?.componentsObjects[0]
          ? cmp.needHelpLinks.componentsObjects[0].urlLocalized
          : '/faq-general';
        const needHelpIcon = {
          iconOnly: this.config.components?.header?.help?.iconOnly,
          iconName: 'icon-help',
          link: {
            url,
            linkName: this.translateService.instant('navigation.help'),
            external: false
          }
        };
        if (!this.headerIconLink.find((icon) => icon.iconName === 'icon-help')) {
          this.headerIconLink.push(needHelpIcon);
        }
      }
    }
  }

  private toNavigationNode(links: NavigationBlocModuleInterface): INavigationNode {
    return {
      children: links.componentsObjects.map((link: CustomCmsLinkComponent) => this.toNavigationChild(link)),
      linksList: [],
      title: this.translateService.instant('navigation.help'),
      type: SubcategoryTypeEnum.defaultLinkList,
      uid: links.uid,
      highlight: !!links?.highlight
    };
  }

  private toNavigationChild(link: CustomCmsLinkComponent): INavigationNode {
    return {
      linksList: [
        {
          url: link.urlLocalized,
          external: false
        }
      ],
      title: link.linkName,
      type: SubcategoryTypeEnum.defaultLinkList,
      uid: link.uid
    };
  }

  private initLanguages(): void {
    combineLatest([this.languageService.getAll(), this.languageService.getActive()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([languages, active]) => {
        this.languageSelectorData = {
          activeIsoCode: active,
          languages: languages.map((lang) => ({
            isoCode: lang.isocode,
            nativeName: lang.nativeName
          }))
        };
      });
  }

  private initCartDropdown(): void {
    this.activeCartService
      .getActiveCartId()
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartId: string) => (this.cartId = cartId));

    this.activeCartService
      .getActive()
      .pipe(
        filter((cart) => !!cart),
        takeUntil(this.destroy$)
      )
      .subscribe((cart) => {
        this.setCurrentCart(cart, this.cmsService);
      });

    this.setCurrentCartTracking();
  }

  private setBundleToSticky(bundle: Bundle): void {
    this.currentBundle = bundle;
    const isInStock = !!bundle.stock;
    const hasDiscountedPrice: boolean =
      !!bundle.price?.value && bundle.discountedPrice?.value
        ? bundle.discountedPrice?.value !== bundle.price?.value
        : bundle.discountedPrice?.formattedValue !== bundle.price?.formattedValue;
    this.pageNavigationData = {
      title: '',
      links: [{ url: bundle.canonicalURL }],
      product: {
        title: bundle.name,
        code: bundle.id,
        stock: {
          online: isInStock
        },
        onSale: bundle?.products.every((p) => !!p && p.onSale === true && !!p.price),
        price: bundle?.price?.formattedValue,
        discountedPrice:
          !!bundle?.discountedPrice && hasDiscountedPrice ? bundle?.discountedPrice?.formattedValue : undefined
      }
    };
  }

  onClickPromo(promo: IPromo): void {
    this.trackingService.sendEvent('promotionClick', {
      promotionName: promo.promo,
      promotionPosition: 'push promo minicart'
    });
  }

  openRecommendedPriceModal() {
    this.isOpenedRecommendedModal = true;
  }

  onPushCardClicked(event: IPushCard) {
    if (event?.isPromo) {
      this.trackingService.sendEvent('promotionClick', {
        promotionName: event?.richParagraph.heading.data.title,
        promotionPosition: 'push promo menu',
        promotionType: 'Menu'
      });
    }
  }

  onLinkClicked(event) {
    if (event.level) {
      const navLevel = this.isHob && !event.url?.toUpperCase().startsWith(this.activeBrand)
        ? `${this.activeBrand}${event.url}`
        : event.url;
      this.trackingService.sendEvent('clickNavigation', { navLevel });
    } else {
      this.sendSearchSugestion(event);
    }
    if (event.isBrandCorner) {
      const eventType = this.isHob ? 'clickCrossBrand' : 'clickBrandCorner';
      this.trackingService.sendEvent(eventType, {
        productCategory: event.category,
        positionLabel: 'menu'
      });
    }
  }

  onClickCardFrames(event) {
    this.trackingService.sendEvent('clickCardFrames', {
      componentName: event.richParagraph.links[0]?.linkName
    });
  }

  ngOnDestroy(): void {
    this.editProfil$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectHob(brand: BrandsEnum) {
    this.trackingService.sendEvent('clickNavigation', {
      navLevel: brand
    });
  }
}
